import { useRef, useEffect, useState } from 'react'
import { FormattedPatient } from '../Patients'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  fetchPatientsAsync,
  ProvenanceData,
  selectPatientsCurrentPage,
  selectPatientsPageSize,
  selectPatientsStatus,
  setRedirectTo,
} from '../patientsSlice'
import {
  sendSxpMessage,
  startSxpProxy,
  updateSxpState,
} from '../../../utils/api'
import {
  ADMIN_PROJECT_ID,
  CHAT_PROJECT_ID,
  REGISTRATION_PROJECT_ID,
} from '../../../utils/constants'
import KeycloakService from '../../../utils/keycloakService'
import { NavLink, useNavigate } from 'react-router-dom'
import MaskedNumber from '../../../components/MaskedNumber'
import {
  PATIENT_DASHBOARD_READ,
  PATIENT_DASHBOARD_WRITE,
  PATIENTS_WRITE,
  SXP_CHAT,
} from '../../../utils/roles'
import { Practitioner, Provenance } from 'fhir/r4'
import ProvenanceView from '../../../components/ProvenanceView'
import { getProvenanceData } from '../utils'
import { loading, odooPharmacyLink } from '../../appointments/models'
import { selectSelectedLocationId } from '../../location/locationSlice'
import LocationByPrefix from '../dashboard/LocationByPrefix'
import { ColDef, INumberFilterParams } from 'ag-grid-community'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { Box, Button, IconButton, Tooltip, useTheme } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import MedAgDataGrid from '../../../components/MedAgDataGrid'
import PatientSearch from './PatientSearch'
import CustomHeader from '../../Radiology/components/RadiologyModel/CustomHeader'
import { pageSizeOptions } from '../../Radiology/Constants/constants'
import { bulkRegistrationValue, REGISTRATION_TYPES, unpaid } from '../constants'
import { create, update } from '../../lms/constants'
import { opdRoute } from '../../OPDScreen/constant'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EditNoteIcon from '@mui/icons-material/EditNote'
import BiotechIcon from '@mui/icons-material/Biotech'
import { toast } from 'react-toastify'
import {
  ArchivePatient,
  BulkRegistration,
  deletePatient,
} from '../../../services/PatientService'
import BulkRegistrationDialog from './BulkRegistrationDialog'
import { dropDownMenuKeys } from '../../../components/constants'
import referral from '../../../assets/icons/referral.svg'
import ReferralForm from '../../ReferralDetails/ReferralForm'
type Props = {
  patients: FormattedPatient[]
}

const styles = () => ({
  patientheading: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  deviceText: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  centerHeader: {
    color: '#ffffff',
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
    },
  },
  registrationText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  patientNavigateLink: {
    display: 'inline-block',
    maxWidth: '220px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
  },
  calendarMonthIcon: {
    color: '#625e5e !important',
    width: '20px',
    height: '20px',
  },
  paidLink: {
    padding: '2px 4px',
    backgroundColor: 'red',
    color: 'white',
    borderRadius: 2,
    marginLeft: 6,
    fontSize: 11,
    cursor: 'pointer',
  },
  endIcon: {
    fontSize: '13px !important',
  },
  actionCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  unpaidLink: {
    textTransform: 'initial',
    outline: 'border-box',
    background: '#E35335',
    color: 'white',
    padding: '0px 2px',
    fontSize: '10px',
    '&:hover': {
      background: '#E35335',
    },
  },
})

const RegistrationType = ({ regType }: { regType: string }) => {
  const { text, title } = REGISTRATION_TYPES[regType] || {
    text: 'Other',
    title: 'Invalid Registration Type',
  }

  return (
    <span className='registration-text' title={title}>
      {text}
    </span>
  )
}

const PatientTable = ({ patients }: Props) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const status = useAppSelector(selectPatientsStatus)
  const locationId = useAppSelector(selectSelectedLocationId)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [provenances, setProvenances] = useState<
    Record<string, ProvenanceData>
  >({})
  const [apiLoading, setApiLoading] = useState(false)
  const ids = patients?.map((p) => p?.fhirId).join(',')
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null
  )
  const [openBulkDialog, setOpenBulkDialog] = useState(false)
  const [referralPatient, setReferralPatient] = useState<{
    uhid: string
    name: string
    fhirId: string
  } | null>(null)

  useEffect(() => {
    if (ids) {
      startSxpProxy(REGISTRATION_PROJECT_ID, 'fetchProvenance', {
        patientIds: ids,
      })
        .then((data) => {
          const entries =
            data.data?.entry?.map(
              (dde: { resource: Provenance | Practitioner }) => dde.resource
            ) ?? []
          const obj = getProvenanceData(entries)
          setProvenances(obj)
        })
        .catch((err) => console.log(err))
    }
  }, [ids])

  const createAppointment = async (d: FormattedPatient) => {
    if (KeycloakService.hasRole([SXP_CHAT])) {
      await sendSxpMessage(CHAT_PROJECT_ID, '::restart')
      const state = {
        patientId: d.fhirId,
        identifier: d.id,
      }
      await updateSxpState(CHAT_PROJECT_ID, state)
      window.chatSendMsg('bookAppointmentFromUI', KeycloakService.getUsername())
    }
  }

  const editPatient = (d: FormattedPatient) => {
    if (KeycloakService.hasRole([PATIENTS_WRITE])) {
      dispatch(setRedirectTo('patients'))
      navigate(`/patient-registration/${d.fhirId}`)
    }
  }
  const handleSelect = (e: any) => {
    if (e?.[0] === dropDownMenuKeys?.delete) {
      deleteOrder()
    }
    if (e?.[0] === dropDownMenuKeys?.archive) {
      ArchiveOrder()
    }
    if (e?.[0] === bulkRegistrationValue) {
      setOpenBulkDialog(true)
    }
  }
  const deleteOrder = async () => {
    setApiLoading(true)
    const response = await deletePatient(selectedPatientId)
    if (response?.success) {
      setApiLoading(false)
      toast.success(response?.message)
      setSelectedPatientId(null)
      dispatch(fetchPatientsAsync())
    } else {
      setApiLoading(false)
      toast.error(response?.message)
      setSelectedPatientId(null)
    }
  }
  const ArchiveOrder = async () => {
    setApiLoading(true)
    const response = await ArchivePatient(selectedPatientId)
    if (response?.success) {
      setApiLoading(false)
      toast.success(response?.message)
      setSelectedPatientId(null)
      dispatch(fetchPatientsAsync())
    } else {
      setApiLoading(false)
      toast.error(response?.message)
      setSelectedPatientId(null)
    }
  }

  const patientTableContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const resizeHandler = () => {
      const container = patientTableContainerRef.current
      if (container) {
        const availableHeight: any =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 60}px`
      }
    }

    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])
  const RenderName = (params: any) => {
    const d = params?.data
    const handleNameClick = () => {
      navigate(opdRoute)
    }

    return (
      <>
        {KeycloakService.hasRole([
          PATIENT_DASHBOARD_READ,
          PATIENT_DASHBOARD_WRITE,
        ]) ? (
          <NavLink
            to='/opd'
            state={{ fhirId: d?.fhirId, MUHId: d?.id }}
            onClick={handleNameClick}
            className={classes?.patientNavigateLink}
          >
            {d.name}
          </NavLink>
        ) : (
          d?.name
        )}{' '}
      </>
    )
  }
  const RenderMobile = (params: any) => {
    const d = params?.data
    return <MaskedNumber mobile={d?.mobile} />
  }
  const RenderLocation = (params: any) => {
    const d = params?.data
    return <LocationByPrefix id={d?.id} />
  }
  const RenderCreatedOn = (params: any) => {
    const d = params?.data
    return (
      <ProvenanceView
        date={d?.registeredOn}
        name={provenances?.[d?.fhirId]?.createdBy}
        mode={create}
      />
    )
  }

  const RenderUpdatedOn = (params: any) => {
    const d = params?.data
    return (
      <ProvenanceView
        date={provenances?.[d?.fhirId]?.updatedOn ?? d?.registeredOn}
        name={
          provenances?.[d?.fhirId]?.updatedBy ??
          provenances?.[d?.fhirId]?.createdBy
        }
        mode={update}
      />
    )
  }
  const RenderActions = (params: any) => {
    const patient: FormattedPatient = params?.data
    return (
      <div className={classes?.actionCell}>
        <Tooltip title='Create Appointment'>
          <IconButton
            size='small'
            onClick={
              KeycloakService.hasRole([SXP_CHAT])
                ? () => createAppointment(patient)
                : undefined
            }
            disabled={!KeycloakService.hasRole([SXP_CHAT])}
            data-testid='create-appointment-btn'
          >
            <CalendarMonthIcon
              fontSize='small'
              className={classes?.calendarMonthIcon}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title='Edit Patient'>
          <IconButton
            size='small'
            onClick={
              KeycloakService.hasRole([PATIENTS_WRITE])
                ? () => editPatient(patient)
                : undefined
            }
            disabled={!KeycloakService.hasRole([PATIENTS_WRITE])}
            className={`custom-btn custom-btn-primary ml10 ${
              KeycloakService.hasRole([PATIENTS_WRITE])
                ? 'cursor-pointer'
                : 'auth-disabled cursor-default'
            }`}
          >
            <EditNoteIcon
              fontSize='small'
              className={classes?.calendarMonthIcon}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title='Create Lab Order'>
          <IconButton
            size='small'
            onClick={() =>
              navigate(`/lms/search/createorder/${patient?.fhirId}`)
            }
          >
            <BiotechIcon
              fontSize='small'
              className={classes?.calendarMonthIcon}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title='Add Referral'>
          <IconButton
            size='small'
            onClick={() =>
              setReferralPatient({
                uhid: patient?.id,
                name: patient?.name,
                fhirId: patient?.fhirId,
              })
            }
          >
            <img src={referral} alt='Referral' width={26} height={26} />
          </IconButton>
        </Tooltip>
      </div>
    )
  }
  const RenderPaymentStatus = (params: any) => {
    const d = params?.data
    const openOdooInvoice = (id: string) => {
      startSxpProxy(ADMIN_PROJECT_ID, odooPharmacyLink, {
        invoiceId: id,
        location: locationId,
      })
        .then((data) => {
          const url = data?.data?.auth_link
          window.open(url, '_blank', 'noopener,noreferrer')
        })
        .catch((err) => console.error(err, 'err'))
    }
    return (
      <div>
        {d.invoiceId && d.invoiceId !== unpaid ? (
          <Button
            size='small'
            onClick={() => openOdooInvoice(d?.invoiceId)}
            className={classes?.unpaidLink}
            endIcon={<OpenInNewIcon className={classes?.endIcon} />}
          >
            Unpaid
          </Button>
        ) : (
          '-'
        )}
      </div>
    )
  }
  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'checkbox',
      width: 60,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { textAlign: 'center' },
      checkboxSelection: false, // AG Grid built-in selection is off
      cellRenderer: (params: any) => {
        const d = params?.data
        return (
          <input
            type='checkbox'
            width={'10px'}
            height={'10px'}
            className='checkbox-input'
            checked={selectedPatientId === d?.fhirId}
            onChange={() => {
              setSelectedPatientId(
                selectedPatientId === d?.fhirId ? null : d?.fhirId
              )
            }}
          />
        )
      },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'SL No',
      field: 'slNo',
      minWidth: 80,
      sortable: true,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      valueGetter: (params: any) => {
        const { currentPage, pageSize } = params.context
        return (currentPage - 1) * pageSize + (params?.node?.rowIndex + 1)
      },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'UHID',
      field: 'id',
      flex: 1,
      minWidth: 120,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Reg.Type',
      field: 'registrationType',
      minWidth: 130,
      sortable: false,
      filter: false,
      headerComponent: CustomHeader,
      cellRenderer: (params: any) => (
        <RegistrationType regType={params?.data?.registrationType} />
      ),
      cellClass: 'custom-header-cell',
      headerClass: classes?.centerHeader,
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Name',
      field: 'name',
      minWidth: 300,
      sortable: true,
      filter: true,
      cellRenderer: RenderName,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
      },
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Mobile No',
      field: 'mobile',
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: RenderMobile,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Gender',
      field: 'gender',
      minWidth: 100,
      sortable: true,
      headerComponent: CustomHeader,
      filter: false,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
    },
    {
      headerName: 'Age',
      field: 'age',
      minWidth: 80,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
    },

    {
      headerName: 'Operating Unit',
      field: 'operatingUnit',
      minWidth: 160,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellRenderer: RenderLocation,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
    },
    {
      headerName: 'Pay.Status',
      field: 'actions',
      width: 110,
      sortable: false,
      filter: false,
      cellRenderer: RenderPaymentStatus,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Action',
      field: 'actions',
      minWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: RenderActions,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        gap: '1px',
        justifyContent: 'center',
      },
    },
    {
      headerName: 'Created By',
      field: 'registeredOn',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: RenderCreatedOn,
      headerComponent: CustomHeader,
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '10px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
      },
      wrapText: true,
      autoHeight: true,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
    },
    {
      headerName: 'Updated By',
      field: 'actions',
      flex: 1,
      headerComponent: CustomHeader,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: RenderUpdatedOn,
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '10px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
      },
      wrapText: true,
      autoHeight: true,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
    },
  ]
  const handleBulkSubmit = async (formData: FormData) => {
    const response = await BulkRegistration(formData)
    if (response?.success) {
      toast.success(response?.message)
      setOpenBulkDialog(false)
    } else {
      toast.error(response?.message)
      setOpenBulkDialog(false)
    }
  }
  return (
    <>
      <BulkRegistrationDialog
        open={openBulkDialog}
        onClose={() => setOpenBulkDialog(false)}
        onSubmit={handleBulkSubmit}
      />
      <ReferralForm
        open={referralPatient !== null}
        onClose={() => setReferralPatient(null)}
        patient={referralPatient ?? undefined}
      />

      <ReferralForm
        open={referralPatient !== null}
        onClose={() => setReferralPatient(null)}
        patient={referralPatient ?? undefined}
      />
      <Box height={'100%'} width={'100%'}>
        <MedAgDataGrid
          tableHeading={'OP Registration'}
          tableHeight={'100%'}
          rowData={status === loading ? [] : patients}
          columnDefs={columnDefs}
          pagination={false}
          pageSizeOptions={pageSizeOptions}
          exportIcon={false}
          searchField={false}
          headerHeight={30}
          filterActions={
            <PatientSearch
              selectedPatientId={selectedPatientId}
              onSelect={handleSelect}
            />
          }
          localSearch={false}
          enableCellTextSelection={true}
          context={{
            currentPage: useAppSelector(selectPatientsCurrentPage),
            pageSize: useAppSelector(selectPatientsPageSize),
          }}
          loading={status === loading || apiLoading ? true : false}
        />
      </Box>
    </>
  )
}

export default PatientTable
